<template>
  <VSkeletonLoader
    grid-list-lg
    fluid
    height="auto"
    type="table-heading,card"
    :loading="isReportLoading"
    :class="{
      'mt-3': isReportLoading,
    }"
  >
    <VRow 
      ref="overview"
      class="deliverable-report justify-center"
    >
      <VCol cols="10">
        <VRow 
          v-if="report"
          class="mx-0"
        >
          <VCol 
            cols="12"
            class="py-3"
          >
            <h2 style="font-size: 23px;">
              {{ model.title }}
            </h2>
            <p
              v-if="model.content"
              v-linkified
              v-html="model.content"
              class="formatted pt-2 pr-5"
              style="font-size: 15px;"
            />
            <FilesList
              v-if="model.files.length"
              class="mt-4"
              :readonly="true"
              :files="model.files"
            />
          </VCol>
          <VCol 
            cols="12"
            class="py-3"
          >
            <VCard class="title-border-none">
              <VCardTitle class="no-title-border">
                <h2 style="font-size: 18px;">
                  Total {{ featureName(modelType, 'capitalize') }} Responses
                </h2>
              </VCardTitle>
              <VCardText class="pt-0">
                <VCard
                  color="#FFC100"
                  height="40px"
                  class="d-flex align-center justify-center rounded-pill font-weight-bold mx-2"
                  :style="summaryBarStyle"
                >
                  Of the {{ report.summary.total_started + report.summary.total_completed }} users who started this form, {{ report.summary.total_completed }} ({{ report.summary.completion_rate }}) completed it.
                </VCard>
                <VRow class="mx-0 mt-2">
                  <VCol 
                    cols="12"
                    class="pb-0"
                  >
                    <h2 style="font-size: 18px;">
                      Form Responses Over Time
                    </h2>
                    <h4 
                      class="font-weight-regular pt-1 pb-6"
                      style="font-size: 15px;"
                    >
                      Users who started and completed the form in the selected time period.
                    </h4>
                    <highcharts :options="summaryChartOptions" />
                  </VCol>
                </VRow>
              </VCardText>
            </VCard>
          </VCol>
          <VCol
            v-for="(metadataItem, metadataIndex) in report.metadata" 
            cols="12"
            class="py-3"
            :key="metadataIndex"
          >
            <VDivider 
              class="mt-6 mb-8" 
              style="background-color: black;" 
            />
            <h2 class="text-h5 font-weight-bold">
              {{ metadataItem.page }}
            </h2>
            <p
              v-if="metadataItem.description"
              v-linkified
              v-html="metadataItem.description"
              class="formatted pt-2"
              style="font-size: 15px;"
            />
            <VRow>
              <VCol 
                v-for="(item, key) in metadataItem.data"
                cols="12"
                class="py-3"
                :key="key"
                :ref="`${metadataIndex + 1}-${item.question.id}`"
              >
                <h2 class="text-h6 font-weight-bold pb-2">
                  {{ item.question.name || '(no name)' }}
                </h2>
                <VCard class="title-border-none">
                  <VCardTitle class="no-title-border">
                    <h2 style="font-size: 18px;">
                      Responses
                    </h2>
                  </VCardTitle>
                  <VCardText class="py-0 px-4">
                    <h4 
                      v-if="item.question.is_required"
                      class="mb-4"
                      style="font-size: 15px;"
                    >
                      This question required a response to submit the form.
                    </h4>
                    <VCard
                      v-else
                      color="#FFC100"
                      height="40px"
                      class="d-flex align-center justify-center rounded-pill font-weight-bold mx-2 mb-4"
                      :style="getMetadataBarStyle(item.response_rate)"
                    >
                    {{ item.total_respondents }} of {{ item.total_completed }} ({{ item.response_rate }}) form respondents answered this question.
                    </VCard>
                    <highcharts 
                      v-if="optionalQuestionsTypes.includes(item.question.type)"
                      :options="getMetadataChartOptions(item)" 
                    />
                    <DataViewUI
                      hide-actions
                      hide-main-title
                      hide-item-actions
                      hide-records-info
                      style="max-height: 652px; overflow-y: auto;"
                      :data="{
                        data: item.responses,
                      }"
                      :headers="metadataResponsesHeaders(item.question)"
                    >
                      <template #items-cells="props">
                        <td 
                          v-if="!optionalQuestionsTypes.includes(item.question.type)"
                          class="simple"
                          :style="{
                            'cursor': optionalQuestionsTypes.includes(item.question.type) ? 'auto' : 'pointer',
                          }"
                          @click="showFullResponse(item, props.item, props.index)"
                        >
                          <UserModal :user="props.item.deliverables_submitted.user" />
                        </td>
                        <td 
                          v-if="!optionalQuestionsTypes.includes(item.question.type)"
                          :style="{
                            'cursor': optionalQuestionsTypes.includes(item.question.type) ? 'auto' : 'pointer',
                          }"
                          @click="showFullResponse(item, props.item, props.index)"
                        >
                          {{ $timezone.formatDateTime(props.item.deliverables_submitted.submitted_on) }}
                        </td>
                        <td 
                          :class="{
                            'simple': optionalQuestionsTypes.includes(item.question.type),
                          }"
                          :style="{
                            'cursor': optionalQuestionsTypes.includes(item.question.type) ? 'auto' : 'pointer',
                          }"
                          @click="showFullResponse(item, props.item, props.index)"
                        >
                          <div class="d-flex align-center my-4">
                            <b 
                              v-if="optionalQuestionsTypes.includes(item.question.type) && item.question.type != 'Rating'"
                              class="text-h6 font-weight-bold mr-6"
                            >
                              {{ String.fromCharCode(getSelectedOptionIndex(item.question, props.item.value) + 65) }}
                            </b>
                            <div 
                              v-if="optionalQuestionsTypes.includes(item.question.type)"
                              class="text-wrap"
                            >
                              {{ props.item.value }} {{ item.question.type == "Rating" ? " Star" : "" }}
                            </div>
                            <div 
                              v-else
                              class="text-wrap response"
                            >
                              {{ props.item.answers.value }}
                            </div>
                          </div>
                        </td>
                        <td 
                          v-if="optionalQuestionsTypes.includes(item.question.type)"
                          class="text-center"
                        >
                          {{ props.item.count }}
                        </td>
                        <td 
                          v-if="optionalQuestionsTypes.includes(item.question.type)"
                          class="text-center"
                        >
                          {{ props.item.percent }}%
                        </td>
                      </template>
                    </DataViewUI>
                  </VCardText>
                </VCard>
              </VCol>
            </VRow>
            <infinite-loading
              v-if="report.page < report.total_pages"
              :identifier="new Date()"
              @infinite="getNextPageReport"
            />
          </VCol>
        </VRow>
        <VDialog
          max-width="680px"
          :dismissable="false"
          :persistent="false"
          :value="dialogs.response.status"
          @click:outside="dialogs.response.status = false"
        >
          <VRow
            v-if="dialogs.response.data.hasOwnProperty('metadata')"  
            class="white mx-0 px-7 pt-4"
          >
            <VCol cols="12">
              <h2 class="text-h6 font-weight-bold pb-2">
                {{ dialogs.response.data.metadata.question.name || '(no name)' }}
              </h2>
              </VCol>
            <VCol 
              cols="12"
              class="py-0"
            >  
              <VCarousel 
                v-model="dialogs.response.data.index"
                hide-delimiters
                height="100%"
                next-icon="mdi-arrow-right"
                prev-icon="mdi-arrow-left"
                class="d-flex justify-center response-carousel"
                :continuous="false"
              >
                <VCarouselItem
                  v-for="(response, index) in dialogs.response.data.metadata.responses"
                  :key="index"
                >
                  <VCard 
                    color="rgba(255, 193, 0, 0.2)"
                    width="540px"
                    class="py-3 px-8"
                  >
                    <VCardText>
                      <div class="d-inline-flex align-center">
                        <VAvatar
                          color="primary"
                          size="68px"
                          class="mb-3 mr-4"
                        >
                          <img :src="response.deliverables_submitted.user.photo_url">
                        </VAvatar>
                        <h3 style="font-size: 19px;">
                          {{ response.deliverables_submitted.user.name }}
                        </h3>
                      </div>
                      <p class="text-subtitle-2">
                        {{ response.answers.value }}
                      </p>
                      <p class="text-subtitle-2 font-weight-bold">
                        Responded on {{ $timezone.formatDateTime(response.deliverables_submitted.submitted_on) }}
                      </p>
                    </VCardText>
                  </VCard>
                  <h4 
                    class="text-center py-8"
                    style="font-size: 15px;"
                  >
                    Response {{ dialogs.response.data.index + 1 }} of {{ dialogs.response.data.metadata.responses.length }}
                  </h4>
                </VCarouselItem>
              </VCarousel>
            </VCol>
          </VRow>
        </VDialog>
      </VCol>
    </VRow>
  </VSkeletonLoader>
</template>
<script>
import { mapActions } from "vuex";
import ViewMixin from "@/mixins/View";
export default {
  mixins: [ViewMixin],
  data() {
    return {
      modelType: "deliverable",
      report: null,
      isFirstLoad: true,
      ratings: [1, 2, 3, 4, 5],
      leftMenuItemsForReport: [],
      optionalQuestionsTypes: ['Checkbox', 'Dropdown', 'Radio', 'Rating'],
      restrictedTypes: ['Url', 'Google Drive'],
      filter: {
        start: null,
        end: null,
        programs: [],
      },
      dialogs: {
        response: {
          data: {},
          status: false,
        },
      },
    };
  },
  watch: {
    isReportLoading(value) {
      if(!value && this.isFirstLoad) {
        this.isFirstLoad = false;
        this.report.related_programs.unshift({
          id: 'none',
          title: "None",
        });
        this.$bus.$emit("reportDetails", {
          ...this.report,
          model: this.model,
          modelType: this.modelType,
        });
      }
    },
    '$route.hash': {
      handler: function(value) {
        if(value) {
          this.$nextTick(() => {
            this.scrollToElement(value);
          });
        }
      },
    },
  },
  computed: {
    isReportLoading() {
      return !this.modelHasId || !this.report;
    },
    summaryBarStyle() {
      return {
        background: `linear-gradient(90deg, #FFC100 0%, #FFC100 ${this.report.summary.completion_rate}, #D6D6D6 ${this.report.summary.completion_rate})`,
        fontSize: '15px',
      };
    },
    summaryChartOptions() {
      return {
        xAxis: {
          type: 'datetime',
          labels: {
            format: '{value:%d %b %Y}'
          },
          categories: this.report.summary.details.map((detail) => {
            return Date.parse(detail.date);
          }),
        },
        yAxis: {
          title: {
            text: 'Number of Responses',
          }
        },
        series: [
          {
            name: 'Form Started',
            data: this.report.summary.details.map((detail) => {
              return detail.started;
            }),
          }, 
          {
            name: 'Form Completed',
            data: this.report.summary.details.map((detail) => {
              return detail.completed;
            }),
          },
        ],
        tooltip: {
          xDateFormat: '%d %B %Y',
        },
      };
    },
    hasFilters() {
      return this.filter.programs.length || (this.filter.start && this.filter.end);
    },
  },
  created() {
    this.$bus.$emit("deliverableReportMenuItems", this.leftMenuItemsForReport);
    if(this.$route.params.hasOwnProperty('programs')) {
      this.filter.programs = this.$route.params.programs;
    }
    this.getNextPageReport();
    this.$bus.$on('filterReport', (dateRange, programs) => {
      if(dateRange == 'all') {
        this.filter.start = null;
        this.filter.end = null;
      } else {
        this.filter.start = this.$timezone.moment().subtract(dateRange, 'days').format('YYYY-MM-DD');
        this.filter.end = this.$timezone.moment().format('YYYY-MM-DD');
      }
      this.report = null;
      this.filter.programs = programs;
      this.getNextPageReport();
    });
  },
  beforeDestroy() {
    this.$bus.$off('filterReport');
  },
  methods: {
    ...mapActions({
      doGetDeliverable: "deliverable/getOne",
      doGetDeliverableReport: "report/deliverable",
    }),
    onGet(id) {
      return this.doGetDeliverable(id).then((result) => {
        this.prepareLeftSideMenu(result);
        return result;
      });
    },
    prepareLeftSideMenu(deliverable) {
      let sections =  deliverable.metadata
                        .filter((input) => !this.restrictedTypes.includes(input.type))
                        .groupBy((input) => input.section);
      Object.keys(sections || {})
        .unique()
        .map((section, index) => {
          const subGroupItems = sections[section].map((metadata) => {
            return {
              enabled: true,
              title: metadata.name || '(no name)',
              route: {
                name: 'deliverable.report',
                params: {
                  id: this.$route.params.id,
                  team: this.$team.slug,
                },
                hash: `#${index + 1}-${metadata.id}`,
              },
            };
          });
          this.leftMenuItemsForReport.push({
            title: section,
            enabled: true,
            icon: false,
            isImage: false,
            subGroup: true,
            category: "deliverableReport",
            routeGroup: "deliverableReport",
            routeName: "deliverable.report",
            subGroupItems: subGroupItems,
          });
        });
      this.$bus.$emit("deliverableReportMenuItems", this.leftMenuItemsForReport);
    },
    getMetadataChartOptions(metadata) {
      let seriesData = [];
      if(metadata.question.type == 'Rating') {
        seriesData = this.ratings.map((rating) => {
          const selectedOption = metadata.responses.find((response) => response.value == rating);
          if(selectedOption) {
            return selectedOption.count;
          }
          return 0;
        })
      } else {
        seriesData = metadata.question.options.options.map((option, index) => {
          const selectedOption = metadata.responses.find((response) => response.value == option);
          if(selectedOption) {
            return selectedOption.count;
          }
          return 0;
        });
      }
      return {
        chart: {
          type: 'column',
        },
        legend: {
          enabled: false
        },
        plotOptions: {
          column: {
            pointWidth: 70,
          },
        },
        xAxis: {
          categories: metadata.question.type == 'Rating' ? this.ratings.map((rating) => rating + " Star") : [...Array(metadata.question.options.options.length).keys()].map(i => String.fromCharCode(i + 65)),
        },
        yAxis: {
          allowDecimals: false,
          title: {
            text: 'Number of Responses',
          }
        },
        series: [
          {
            data: seriesData,
          },
        ],
      };
    },
    metadataResponsesHeaders(metadata) {
      const isOptionalQuestion = this.optionalQuestionsTypes.includes(metadata.type);
      return [
        {
          text: "User",
          align: "left",
          value: "",
          sortable: true,
          enabled: !isOptionalQuestion,
        },
        {
          text: "Submitted On",
          align: "left",
          value: "",
          sortable: true,
          enabled: !isOptionalQuestion,
        },
        {
          text: "Response",
          align: "left",
          value: "",
          sortable: false,
          enabled: true,
        },
        {
          text: "Count",
          align: "center",
          value: "",
          sortable: false,
          enabled: isOptionalQuestion,
        },
        {
          text: "Percent",
          align: "center",
          value: "",
          sortable: false,
          enabled: isOptionalQuestion,
        },
      ].filter((item) => item.enabled === true);
    },
    showFullResponse(metadata, submittedData, index) {
      if(!this.optionalQuestionsTypes.includes(metadata.question.type)) {
        this.dialogs.response.data = {
          index,
          metadata,
          submittedData,
        };
        this.dialogs.response.status = true;
      }
    },
    getMetadataBarStyle(responseRate) {
      return {
        background: `linear-gradient(90deg, #FFC100 0%, #FFC100 ${responseRate}, rgba(255, 193, 0, 0.2) ${responseRate})`,
        fontSize: '15px',
      };
    },
    getSelectedOptionIndex(question, selectedOption) {
      if(question.type == 'Rating') {
        return this.ratings.indexOf(selectedOption);
      } else {
        return question.options.options.indexOf(String(selectedOption));
      }
    },
    getNextPageReport($infinite) {
      const params = {
        id: this.$route.params.id,
        page: this.report ? this.report.page + 1 : 1,
        ...this.filter,
      };
      this.doGetDeliverableReport(params).then((result) => {
        if(this.report) {
          this.report.page = result.page;
          this.report.total_pages = result.total_pages;
          const preparedMetadata = this.prepareMetadata(result.meta_data);
          if(preparedMetadata) {
            this.report.metadata.push(preparedMetadata);
          }
        } else {
          this.report = {
            ...result,
            metadata: [
              this.prepareMetadata(result.meta_data),
            ]
          };
        }
        // Load all pages one by one in the background to support hash navigation i.e. auto-scroll
        if(this.report.page < this.report.total_pages) {
          this.getNextPageReport();
        }
        if(this.$route.hash != "#overview" && this.$route.hash.replace("#", "").split("-")[0] == this.report.page) {
          this.$nextTick(() => {
            this.scrollToElement(this.$route.hash);
          });
        }
        if($infinite) {
          $infinite.loaded();
          if(this.report.page >= this.report.total_pages) {
            $infinite.complete();
          }
        }
      }).catch(() => {
        this.$router.push({ 
          name: "deliverable.index",
          params: {
            team: this.$team.slug,
          },
        });
      });
    },
    prepareMetadata(metadata) {
      const validMetadata = metadata.data.filter((item) => !this.restrictedTypes.includes(item.question.type));
      if(validMetadata.length) {
        metadata.data = validMetadata.map((item) => {
          const allResponses = [];
          const allOptions = item.question.type == 'Rating' ? this.ratings : item.question.options.options;
          allOptions.map((option) => {
            const selectedOption = item.responses.find((response) => response.value == option);
            if(selectedOption) {
              allResponses.push(selectedOption)
            } else {
              allResponses.push({
                value: option,
                count: 0,
                percent: 0,
              });
            }
          });
          item.responses = allResponses.length ? allResponses : item.responses;
          return item;
        });
        return metadata;
      }
    },
  },
}
</script>
<style lang="scss">
.deliverable-report {
  .v-data-table__wrapper>table>thead>tr>th {
    color: inherit !important;
    border-bottom: 1px solid black !important;
    top: auto !important;
  }
  .response {
    display: -webkit-box;
    overflow: hidden;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
  }
}
.response-carousel {
  .v-window__prev, .v-window__next {
    bottom: 24px;
    margin: 0px 66px;
    top: unset;

    .v-btn {
      background-color: #FFAB00;
      border-radius: 4px;
      height: 44px;
      width: 44px;
      .v-icon {
        font-size: 28px !important;
      }
    }
  }
}
</style>